import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
} from '@angular/core';

export interface ArtifactPickerOption {
  value: string;
  label: string;
  meta?: string;
}

interface ArtifactPickerSegment {
  text: string;
  match: boolean;
}

let nextPickerId: number = 0;

/**
 * Searchable single-value picker for long model and scene lists.
 * Matching is a case-insensitive substring search over the label, meta text and value, so a short id also works.
 */
@Component({
  selector: 'artifact-picker',
  templateUrl: './artifact-picker.component.html',
  styleUrls: ['./artifact-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ArtifactPickerComponent {
  @Input() public options: ArtifactPickerOption[] = [];
  @Input() public value: string | null = null;
  @Input() public placeholder: string = 'Select';
  @Input() public searchPlaceholder: string = 'Search';
  @Input() public disabled: boolean = false;
  @Input() public inputId: string | null = null;
  @Input() public ariaLabel: string | null = null;
  @Output() public readonly valueChange: EventEmitter<string | null> = new EventEmitter<string | null>();

  @ViewChild('searchInput') private readonly searchInput?: ElementRef<HTMLInputElement>;

  public readonly listboxId: string = `artifact-picker-${nextPickerId++}`;
  public open: boolean = false;
  public query: string = '';
  public activeIndex: number = 0;

  public constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly changeDetector: ChangeDetectorRef
  ) {}

  public get selectedOption(): ArtifactPickerOption | undefined {
    return this.options.find((option: ArtifactPickerOption) => option.value === this.value);
  }

  public get filteredOptions(): ArtifactPickerOption[] {
    const query: string = this.query.trim().toLocaleLowerCase();
    if (query.length === 0) {
      return this.options;
    }
    return this.options.filter((option: ArtifactPickerOption) =>
      `${option.label} ${option.meta ?? ''} ${option.value}`.toLocaleLowerCase().includes(query)
    );
  }

  public getOptionId(index: number): string {
    return `${this.listboxId}-option-${index}`;
  }

  public highlight(text: string): ArtifactPickerSegment[] {
    const query: string = this.query.trim();
    const index: number = query.length === 0 ? -1 : text.toLocaleLowerCase().indexOf(query.toLocaleLowerCase());
    if (index < 0) {
      return [{ text, match: false }];
    }
    return [
      { text: text.slice(0, index), match: false },
      { text: text.slice(index, index + query.length), match: true },
      { text: text.slice(index + query.length), match: false },
    ].filter((segment: ArtifactPickerSegment) => segment.text.length > 0);
  }

  public toggle(): void {
    if (this.open) {
      this.close();
    } else {
      this.openPanel();
    }
  }

  public openPanel(): void {
    if (this.disabled) {
      return;
    }
    this.open = true;
    this.query = '';
    const selectedIndex: number = this.options.findIndex((option: ArtifactPickerOption) => option.value === this.value);
    this.activeIndex = Math.max(0, selectedIndex);
    this.changeDetector.markForCheck();
    // The search input is rendered only while the panel is open.
    window.setTimeout(() => {
      this.searchInput?.nativeElement.focus();
      this.scrollActiveOptionIntoView();
    });
  }

  public close(restoreFocus: boolean = false): void {
    this.open = false;
    this.changeDetector.markForCheck();
    if (restoreFocus) {
      this.host.nativeElement.querySelector<HTMLButtonElement>('.artifact-picker__trigger')?.focus();
    }
  }

  public onQueryChanged(event: Event): void {
    if (event.target instanceof HTMLInputElement) {
      this.query = event.target.value;
      this.activeIndex = 0;
    }
  }

  public select(option: ArtifactPickerOption): void {
    this.valueChange.emit(option.value);
    this.close(true);
  }

  public clear(): void {
    this.valueChange.emit(null);
    this.close(true);
  }

  public onTriggerKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.openPanel();
    }
  }

  public onSearchKeydown(event: KeyboardEvent): void {
    const options: ArtifactPickerOption[] = this.filteredOptions;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.activeIndex = options.length === 0 ? 0 : (this.activeIndex + 1) % options.length;
        this.scrollActiveOptionIntoView();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.activeIndex = options.length === 0 ? 0 : (this.activeIndex + options.length - 1) % options.length;
        this.scrollActiveOptionIntoView();
        break;
      case 'Home':
      case 'End':
        event.preventDefault();
        this.activeIndex = event.key === 'Home' ? 0 : Math.max(0, options.length - 1);
        this.scrollActiveOptionIntoView();
        break;
      case 'Enter':
        // Keep Enter from submitting the surrounding form while choosing an option.
        event.preventDefault();
        if (options[this.activeIndex] !== undefined) {
          this.select(options[this.activeIndex]);
        }
        break;
      case 'Escape':
        // Close only the picker, not the dialog that hosts it.
        event.preventDefault();
        event.stopPropagation();
        this.close(true);
        break;
      case 'Tab':
        this.close();
        break;
      default:
        break;
    }
  }

  @HostListener('document:pointerdown', ['$event'])
  public onDocumentPointerDown(event: PointerEvent): void {
    if (this.open && event.target instanceof Node && !this.host.nativeElement.contains(event.target)) {
      this.close();
    }
  }

  private scrollActiveOptionIntoView(): void {
    window.setTimeout(() => {
      this.host.nativeElement
        .querySelector<HTMLElement>(`#${this.getOptionId(this.activeIndex)}`)
        ?.scrollIntoView({ block: 'nearest' });
    });
  }
}
